import React, { useEffect, useState } from 'react';
import { GameMutator, GameState, GameStateOptions, MutationResult } from '../game/game';
import useQueryString from 'use-query-string';
import Game from '../components/Game';
import { CURRENT_DATE } from '../game/CURRENT_DATE';
import useLocalStorage from '../hooks/useLocalStorage';
import useStats from '../hooks/useOverallStats';
import { dehydrateGameStateOptions, hydrateGame } from '../game/hydrateGame';
import { getTheme, Theme } from '../data/themedWords';
import { log, logError } from '../utils/logging';
import { DELETE_EVEN_LETTERS, DUPLICATE_LAST_LETTER, DUPLICATE_MIDDLE_LETTER, Mutator, REMOVE_DUPLICATED_LETTERS, SORT_VOWELS } from '../game/mutators';
import { useAllPlayersStats } from './Stats';

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
declare let __webpack_public_path__: string;
console.log(__webpack_public_path__)
__webpack_public_path__ = window.location.href.replace('index.html','');
console.log(__webpack_public_path__)
const worker = new Worker(new URL('../workers/newGameState2.ts', import.meta.url));

function updateQuery(path: string) {
  window.history.pushState(null, document.title, path);
}

interface DailyGameProps {
  className?: string
  footer?: React.ReactNode
}
function DailyGame(props: DailyGameProps) {
  const qs = useQueryString(
    window.location,
    updateQuery
  );
  const {randomize} = qs[0]
  const baseKey = randomize ? null : CURRENT_DATE
  const [initialState, setInitialState] = useState<GameState|null>(null)
  const [error, setError] = useState<Error|null>(null)
  const {data:allPlayerStats} = useAllPlayersStats()
  useEffect(()=>{
    const seed = randomize ? `${Math.random()}` : CURRENT_DATE
    new Promise((resolve, reject) => {
      worker.onmessage = (event) => {
          if (event.data.error) {
              reject(event.data.error);
          } else {
              resolve(event.data.gameState);
          }
      };
      const gameOptions:GameStateOptions = randomize ? {
        solutionLength: 4,
        randomTheme: true,
        numberOfWords: 6,
        requiresAdditive: true
      } : getDailyOptions(CURRENT_DATE)

      const opts = dehydrateGameStateOptions(gameOptions);
      log({CURRENT_DATE})
      worker.postMessage({ type:'generateGameState', payload:{seed, opts} });
    }).then((gameState:any) => {
      const hydrated = hydrateGame(gameState)
      setInitialState(hydrated);
      setError(null)
    }).catch((e) => {
      setError(e)
      logError(e);
    });
  }, [randomize])

  const {
    currentStreak,
    longestStreak,
    completionPercentage,
    averageMoves,
    setOverallStats
  }= useStats()
  

  const [history, setHistory] = useLocalStorage<string>(baseKey ? `${baseKey}--history` : null,'')
  const [moves, setMoves] = useLocalStorage(baseKey ? `${baseKey}--moves` : null, 0)
  const [anythingTouched, setAnythingTouched] = useLocalStorage(baseKey ? `${baseKey}--anything-touched` : null, false)
  const [isGameComplete, setIsGameComplete] = useLocalStorage(baseKey ? `${baseKey}--complete` : null, false)
  const [isModalOpen, setIsModalOpen] = useState(isGameComplete)
  const [showCopiedBubble, setShowCopiedBubble] = useState(false)

  const formattedHistory = history.replace(/((↩️)+)/g, '\n$1\n')
  const shareLinkText = moves !== 4 ? `I solved Mutatle in ${moves} moves! Can you beat me?\n${formattedHistory}\n` : `I solved Mutatle in a ✨perfect✨ 4 moves! Can you do it too?\n${formattedHistory}\n`

  const loader = <div className={`loading ${(initialState!==null || error!==null) ? 'hidden' : ''}`}><h1>Mutatle</h1></div>

  if(error) return <>
    {loader}
    <div className='error'>
      {randomize ? 'Failed to generate random game' : 'No game today'}
    </div>
  </>
  if(initialState===null) return loader
  return (
    <>
      {loader}
      <Game
        className={`game--daily ${props.className}`}
        footer={props.footer}
        initialState={initialState}
        baseKey={baseKey}
        moves={moves}
        description='Apply four mutations to unscramble the six words'
        disableInput={isModalOpen}
        onUndo={() => {
          if(!isGameComplete) setHistory((currentHistory) => currentHistory + '↩️');
        }}
        onMutatorSelected={(_:GameMutator, result:MutationResult) => {
          if (anythingTouched === false){
            setAnythingTouched(true);
            (async ()=>{
              if(randomize) return
              await fetch('https://www.mutatle.com/Mutatle/update_plays.php', {
                method: 'POST',
              })
            })();
          }
          
          if(!isGameComplete) setMoves(moves + 1)
          const emoji = result ===  MutationResult.Correct ? '🟩' : result ===  MutationResult.WrongPlace ? '🟨' : '⬜'
          if(!isGameComplete) setHistory(history + emoji)
        }}
        onGameCompleted={()=>{
          setIsModalOpen(true);
          if (!isGameComplete) {
            (async () => {
              if (randomize) return;
              await fetch('https://www.mutatle.com/Mutatle/update_completions.php', {
                method: 'POST',
                body: JSON.stringify({ moves: moves + 1 }), // Add one to account for the last move, since the callback is cached before the move is incremented
              });
            })();
            setIsGameComplete(true);
            if(randomize !== null) setOverallStats((current)=>({
              ...current,
              [CURRENT_DATE]: {
                moves: moves + 1, // Add one to account for the last move, since the callback is cached before the move is incremented
                solved: true,
              }
            }))
          }
        }}
      />
      <Modal open={isModalOpen} onClose={()=>setIsModalOpen(false)}>
          {moves === 4 ? <h1>✨Perfect!✨</h1> : <h1>Congratulations!</h1>}
          <p className='summary'>
            You solved today's Mutatle in <b>{moves} moves</b>! 
            {allPlayerStats && allPlayerStats.gamesCompletedToday > 3 && 
              <>
                {' '}
                The average today was <b>{allPlayerStats.averageMovesToday.toFixed(1)} moves</b>,
                and <b>{allPlayerStats.percentPerfectToday.toFixed(1)}%</b> of players solved it a perfect 4 moves {moves===4 && 'like you'}.
              </>
            }
          </p>
          {showCopiedBubble && <div className='copied-bubble'>Copied!</div>}
          <div className='stats'>
            <div className='stat'>
              <div className='stat-value'>{currentStreak}</div>
              <div className='stat-label'>Current streak</div>
            </div>
            <div className='stat'>
              <div className='stat-value'>{longestStreak}</div>
              <div className='stat-label'>Longest streak</div>
            </div>
            <div className='stat'>
              <div className='stat-value'>{Math.floor(completionPercentage)}%</div>
              <div className='stat-label'>Your win<br/> rate</div>
            </div>
            <div className='stat'>
              <div className='stat-value'>{averageMoves.toFixed(1)}</div>
              <div className='stat-label'>Your average moves</div>
            </div>
          </div>

          <button className='share-button' onClick={async ()=>{
            try{
              if(!isMobile) throw new Error('Touch device')
              await navigator.share({
                title: 'Mutatle',
                text: shareLinkText,
                url: "https://www.mutatle.com",
              })
            }catch(e){
              setShowCopiedBubble(false)
              setTimeout(()=>setShowCopiedBubble(true))
              navigator.clipboard.writeText(shareLinkText + "\nhttps://www.mutatle.com")
            }
          }}>Share results</button>
          <a className='penny-ad' href='https://kaisalmon.com/PennyThePumpkin' target='_blank' rel="noreferrer">Penny the pumpkin</a>

      </Modal>
    </>
  );

}

export function Modal(props: {open: boolean, children: React.ReactNode, onClose: ()=>void}){
  return <div className='modal-container'>
    <div className={`modal ${props.open ? 'modal--open' : ''}`}>
    <button onClick={props.onClose} className='modal-close'>Close</button>
    {props.children}
  </div></div>
}


export default DailyGame;
function getDailyOptions(date: string): GameStateOptions {
  type DailyOptions = ({ theme: Theme } | {randomTheme: true} |  {initialWords?: undefined, theme?: undefined, randomTheme?:false}) & {numberOfWords?: number, requiredMutator?: Mutator, requiresAdditive?: boolean, requiredWords?: string[]} 
  const CALENDAR: { [key: string]: DailyOptions } = {
    '2024-10-27': {theme:'christmas' },
    '2024-10-28': {randomTheme: true, requiredMutator: DUPLICATE_MIDDLE_LETTER},
    '2024-10-29': {randomTheme: true, requiredMutator: REMOVE_DUPLICATED_LETTERS},
    '2024-10-30': {randomTheme: true, requiredMutator: SORT_VOWELS},
    '2024-10-31': {theme: 'halloween', requiredWords:['pumpkin']},
    '2024-11-03': {randomTheme: true, requiredMutator: DUPLICATE_LAST_LETTER},
    '2024-11-04': {randomTheme: true, requiredMutator: DELETE_EVEN_LETTERS},
    '2024-11-05': {theme:'fireVerbs', requiredWords:['gunpowder', 'treason', 'plot'] },
    '2024-12-25': {theme:'christmas' },
    '2025-01-01':  {randomTheme: true, requiredWords:['happy', 'new', 'year'], numberOfWords: 3 },
  }
  const todaysOptions = CALENDAR[date]
  if (todaysOptions && 'theme' in todaysOptions) {
    console.log(getTheme((todaysOptions as { theme: Theme }).theme));
  }
  return {
    solutionLength: 4,
    numberOfWords: 6,
    requiresAdditive: true,
    ...(todaysOptions || {randomTheme: true})
  }
}

